import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { auth, db } from '../firebase/config';
import { User, AuthContextType } from '../types';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import { signUpWithEmailAndPasswordRest, updateProfileRest, refreshIdToken } from '../utils/firebaseAuthRestApi';
import { saveTokens, getTokens, clearTokens, updateTokens, isTokenExpired } from '../utils/tokenStorage';
import { userService } from '../services/userService';

const DEBUG_AUTH_CONTEXT = false;
const debugLog = (...args: unknown[]) => {
  if (DEBUG_AUTH_CONTEXT) {
    console.log(...args);
  }
};

// Firebase Authentication plugin - imported dynamically when needed

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userState, setUserState] = useState<User | null>(null);
  
  // Wrapper to ensure user always has id field
  const setUser = (newUser: User | null, firebaseUid?: string) => {
    if (newUser) {
      // CRITICAL: Always ensure id field is set
      if (!newUser.id || newUser.id === undefined || newUser.id === null || newUser.id === '') {
        if (firebaseUid) {
          console.warn('🔴 setUser: user.id is missing, setting from firebaseUid:', firebaseUid);
          newUser.id = firebaseUid;
        } else {
          console.error('🔴 setUser: user.id is missing and no firebaseUid provided! User object:', newUser);
          // Try to get from auth.currentUser as last resort
          try {
            const currentUser = auth.currentUser;
            if (currentUser && currentUser.uid) {
              console.warn('🔴 setUser: Using auth.currentUser.uid as fallback:', currentUser.uid);
              newUser.id = currentUser.uid;
            } else {
              console.error('🔴🔴🔴 CRITICAL: Cannot set user.id - no firebaseUid and no auth.currentUser!');
            }
          } catch (e) {
            console.error('🔴 setUser: Error accessing auth.currentUser:', e);
          }
        }
      }
      debugLog('🔍 setUser: Setting user with id:', newUser.id, 'role:', newUser.role);
      debugLog('🔍 setUser: User object keys:', Object.keys(newUser));
    }
    setUserState(newUser);
  };
  
  const user = userState;
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const [sessionRestored, setSessionRestored] = useState(false); // Track if we've restored from tokens

  // Save pending FCM token and refresh native token when user is set (after login)
  useEffect(() => {
    if (user?.id) {
      debugLog('[AuthContext] User set, attempting to save pending FCM token...');
      const saveToken = async () => {
        try {
          const { notificationService } = await import('../services/notificationService');
          await notificationService.trySavePendingToken(user.id);
          // If the user has notifications enabled on a native platform, silently
          // re-register to ensure the current device token is saved in Firestore
          // (handles fresh installs and APK updates)
          if (user.notificationsEnabled) {
            await notificationService.refreshNativeToken(user.id);
          }
        } catch (error) {
          console.warn('[AuthContext] Could not save/refresh FCM token:', error);
        }
      };
      saveToken();
    }
  }, [user?.id]);

  useEffect(() => {
    debugLog('AuthProvider: Setting up auth state listener');
    setProgress(10); // Initial progress
    
    // Wrap async code in an async function since useEffect callbacks can't be async
    const initializeAuth = async (): Promise<(() => void) | undefined> => {
      // Session restoration: Check for stored tokens and restore user session
      const restoreSession = async (): Promise<boolean> => {
        try {
        debugLog('[SessionRestore] Checking for stored tokens...');
        const tokens = await getTokens();
        
        if (!tokens) {
          debugLog('[SessionRestore] No stored tokens found');
          return false; // No tokens, continue with normal auth flow
        }
        
        debugLog('[SessionRestore] Found stored tokens for user:', tokens.userId);
        setProgress(20);
        
        // Check if token is expired
        const tokenExpired = await isTokenExpired();
        
        let idToken = tokens.idToken;
        let refreshToken = tokens.refreshToken;
        
        if (tokenExpired) {
          debugLog('[SessionRestore] Token expired, refreshing...');
          setProgress(30);
          
          if (!refreshToken) {
            console.warn('[SessionRestore] No refresh token available, clearing tokens');
            await clearTokens();
            return false;
          }
          
          try {
            // Refresh the token
            const refreshResponse = await refreshIdToken(refreshToken);
            idToken = refreshResponse.id_token;
            refreshToken = refreshResponse.refresh_token || refreshToken; // Use new refresh token if provided
            
            // Update stored tokens
            await updateTokens(idToken, refreshToken);
            debugLog('[SessionRestore] Token refreshed successfully');
            setProgress(40);
          } catch (refreshError: any) {
            console.error('[SessionRestore] Token refresh failed:', refreshError);
            
            // If refresh token is expired, clear tokens and show login
            if (refreshError.message === 'REFRESH_TOKEN_EXPIRED') {
              debugLog('[SessionRestore] Refresh token expired, clearing tokens');
              await clearTokens();
              return false;
            }
            
            // Other errors - clear tokens and continue with normal flow
            console.warn('[SessionRestore] Refresh failed, clearing tokens');
            await clearTokens();
            return false;
          }
        } else {
          debugLog('[SessionRestore] Token still valid');
          setProgress(30);
        }
        
        // Fetch user data from Firestore using the token
        debugLog('[SessionRestore] Fetching user data from Firestore...');
        setProgress(50);
        
        const projectId = 'waka-running';
        const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${tokens.userId}`;
        
        const firestoreResponse = await fetch(firestoreUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (firestoreResponse.ok) {
          const firestoreData = await firestoreResponse.json();
          
          if (firestoreData.fields) {
            // Parse user data
            const userData: any = {};
            for (const [key, value] of Object.entries(firestoreData.fields)) {
              const field = value as any;
              let fieldValue: any = null;
              
              if (field.stringValue !== undefined) {
                fieldValue = field.stringValue;
              } else if (field.integerValue !== undefined) {
                fieldValue = parseInt(field.integerValue);
              } else if (field.booleanValue !== undefined) {
                fieldValue = field.booleanValue;
              } else if (field.timestampValue !== undefined) {
                fieldValue = Timestamp.fromDate(new Date(field.timestampValue));
              }
              
              userData[key] = fieldValue;
            }
            
            const user: User = {
              id: tokens.userId,
              email: userData.email || '',
              displayName: userData.displayName || 'User',
              role: (userData.role || 'athlete') as 'coach' | 'athlete' | 'headCoach',
              teamId: userData.teamId || undefined,
              createdAt: userData.createdAt || Timestamp.now(),
              updatedAt: userData.updatedAt || Timestamp.now(),
              profilePrivacy: userData.profilePrivacy,
              fcmToken: userData.fcmToken,
              notificationsEnabled: userData.notificationsEnabled,
              useAthleteWorkflow: userData.useAthleteWorkflow,
              activityChartVariant: userData.activityChartVariant,
              activityChartVariants: userData.activityChartVariants,
              weekStartsOn: userData.weekStartsOn,
            };
            
            debugLog('[SessionRestore] User session restored:', user.id);
            setUser(user);
            setNeedsRoleSelection(userData.needsRoleSelection === true);
            setSessionRestored(true); // Mark that we've restored from tokens
            setProgress(100);
            setLoading(false);
            return true; // Session restored successfully
          } else {
            console.warn('[SessionRestore] User document not found in Firestore');
            await clearTokens();
            return false;
          }
        } else {
          console.error('[SessionRestore] Failed to fetch user data:', firestoreResponse.status);
          // If unauthorized, token might be invalid - clear it
          if (firestoreResponse.status === 401 || firestoreResponse.status === 403) {
            await clearTokens();
          }
          return false;
        }
      } catch (error) {
        console.error('[SessionRestore] Error restoring session:', error);
        // On error, clear tokens and continue with normal flow
        try {
          await clearTokens();
        } catch (clearError) {
          console.error('[SessionRestore] Failed to clear tokens:', clearError);
        }
        return false;
      }
      };
      
      // Try to restore session first (before other auth checks)
      // Only proceed with other auth checks if session restoration failed
      const sessionRestoredSuccessfully = await restoreSession();
      
      // If session was restored successfully, skip all the polling and manual checks
      if (sessionRestoredSuccessfully) {
      debugLog('[SessionRestore] Session restored successfully, skipping Firebase SDK checks');
      // Still set up the auth state listener, but it will respect sessionRestored flag
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        // On iOS, if we've already restored from tokens and auth.currentUser is null,
        // don't let the listener clear the user
        const platform = Capacitor.getPlatform();
        if (!firebaseUser && platform === 'ios' && sessionRestored && user) {
          debugLog('[onAuthStateChanged] iOS: Ignoring null firebaseUser - session was restored from tokens');
          return;
        }
        // Only handle auth state change if we have a user (don't clear on null)
        if (firebaseUser) {
          // Define handleAuthStateChange inline since we're in early return path
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data() as User;
              // Ensure id field is set from document ID
              const userId = userDoc.id || firebaseUser.uid;
              const userWithId: User = {
                id: userId,  // Set id FIRST to ensure it's not overwritten
                email: userData.email || firebaseUser.email || '',
                displayName: userData.displayName || firebaseUser.displayName || '',
                role: userData.role || 'athlete',
                teamId: userData.teamId,
                profilePrivacy: userData.profilePrivacy,
                fcmToken: userData.fcmToken,
                notificationsEnabled: userData.notificationsEnabled,
                useAthleteWorkflow: userData.useAthleteWorkflow,
                activityChartVariant: userData.activityChartVariant,
                activityChartVariants: userData.activityChartVariants,
                weekStartsOn: userData.weekStartsOn,
                createdAt: userData.createdAt,
                updatedAt: userData.updatedAt,
              };
              
              // CRITICAL: Verify id is set before setting user
              if (!userWithId.id || userWithId.id === undefined || userWithId.id === null) {
                console.error('🔴 CRITICAL ERROR: userWithId.id is missing! firebaseUser.uid:', firebaseUser.uid);
                userWithId.id = firebaseUser.uid; // Force set it
              }
              
              setUser(userWithId);
              setNeedsRoleSelection((userData as any).needsRoleSelection === true);
            }
          } catch (error) {
            console.error('Error in auth state listener after session restore:', error);
          }
        }
      });
      
        return () => {
          unsubscribe();
        };
      }
      
      // Only run the following code if session restoration failed
      // On iOS, also poll auth.currentUser after redirect (in case getRedirectResult fails)
      // This helps catch Google redirects that complete but SDK doesn't process
      if (Capacitor.getPlatform() === 'ios') {
      const pollAuthState = async () => {
        debugLog('iOS: Starting polling for auth.currentUser after redirect...');
        // Wait a bit for redirect to process
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Poll for up to 30 seconds (more time for redirect to complete)
        for (let i = 0; i < 60; i++) {
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const currentUser = auth.currentUser;
          if (currentUser) {
            debugLog(`iOS: Polling found user after redirect (attempt ${i + 1}):`, currentUser.uid);
            
            // Check if we already processed this user (avoid duplicate processing)
            if (user && user.id === currentUser.uid) {
              debugLog('iOS: User already processed, skipping');
              break;
            }
            
            // Use REST API workaround to get user data
            try {
              const idToken = await currentUser.getIdToken();
              const userId = currentUser.uid;
              
              if (idToken) {
                // Store tokens (preserve existing refreshToken if available)
                try {
                  const existingTokens = await getTokens();
                  await saveTokens(
                    idToken,
                    existingTokens?.refreshToken || '', // Preserve existing refreshToken if available
                    userId
                  );
                } catch (e) {
                  console.warn('Could not store tokens:', e);
                }
                
                // Use Firestore REST API to fetch/create user document
                const projectId = 'waka-running';
                const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${userId}`;
                
                const firestoreResponse = await fetch(firestoreUrl, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (firestoreResponse.ok) {
                  const firestoreData = await firestoreResponse.json();
                  
                  if (firestoreData.fields) {
                    // User document exists - parse it
                    const userData: any = {};
                    for (const [key, value] of Object.entries(firestoreData.fields)) {
                      const field = value as any;
                      let fieldValue: any = null;
                      
                      if (field.stringValue !== undefined) {
                        fieldValue = field.stringValue;
                      } else if (field.integerValue !== undefined) {
                        fieldValue = parseInt(field.integerValue);
                      } else if (field.booleanValue !== undefined) {
                        fieldValue = field.booleanValue;
                      } else if (field.timestampValue !== undefined) {
                        fieldValue = Timestamp.fromDate(new Date(field.timestampValue));
                      }
                      
                      userData[key] = fieldValue;
                    }
                    
                    const parsedUser: User = {
                      id: userId,
                      email: userData.email || currentUser.email || '',
                      displayName: userData.displayName || currentUser.displayName || 'User',
                      role: (userData.role || 'athlete') as 'coach' | 'athlete' | 'headCoach',
                      teamId: userData.teamId || undefined,
                      createdAt: userData.createdAt || Timestamp.now(),
                      updatedAt: userData.updatedAt || Timestamp.now(),
                      profilePrivacy: userData.profilePrivacy,
                      fcmToken: userData.fcmToken,
                      notificationsEnabled: userData.notificationsEnabled,
                      useAthleteWorkflow: userData.useAthleteWorkflow,
                      activityChartVariant: userData.activityChartVariant,
                      activityChartVariants: userData.activityChartVariants,
                      weekStartsOn: userData.weekStartsOn,
                    };
                    
                    debugLog('iOS: Polling found and set user data:', parsedUser);
                    setUser(parsedUser);
                    setNeedsRoleSelection(userData.needsRoleSelection === true);
                    setProgress(100);
                    setLoading(false);
                    break;
                  }
                }
              }
            } catch (pollError) {
              console.error('iOS: Polling REST API error:', pollError);
            }
            
            break; // Found user, stop polling
          }
          
          if (i % 10 === 0 && i > 0) {
            debugLog(`iOS: Still polling for auth.currentUser... (attempt ${i + 1}/60)`);
          }
        }
        
        debugLog('iOS: Polling completed - no user found via auth.currentUser');
      };
      
      // Start polling in background (don't await)
      pollAuthState().catch(err => console.error('iOS: Polling error:', err));
    }
    
    // Check for redirect result first (with timeout to prevent hanging)
    const checkRedirectResult = async () => {
      try {
        setProgress(20);
        // Add timeout to prevent hanging on CORS errors
        // Increased timeout for iOS and Android as redirect processing can be slower
        const platform = Capacitor.getPlatform();
        const timeoutMs = platform === 'ios' ? 10000 : 10000; // Increased Android timeout to 10s
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Redirect check timeout')), timeoutMs);
        });
        
        debugLog(`Checking redirect result (platform: ${platform}, timeout: ${timeoutMs}ms)...`);
        const result = await Promise.race([
          getRedirectResult(auth),
          timeoutPromise
        ]) as any;
        
        if (result && result.user) {
          debugLog('✅ Redirect result found:', result.user.uid);
          setProgress(40);
          
          const platform = Capacitor.getPlatform();
          
          // On iOS, SDK might not set auth.currentUser properly after redirect
          // Use REST API workaround similar to email/password sign-in
          if (platform === 'ios') {
            debugLog('iOS platform - using REST API workaround for Google redirect');
            setProgress(50);
            
            try {
              // Get idToken from the result
              const idToken = await result.user.getIdToken();
              const userId = result.user.uid;
              
              if (!idToken) {
                throw new Error('No idToken from Google redirect result');
              }
              
              // Store tokens (preserve existing refreshToken if available)
              try {
                const existingTokens = await getTokens();
                await saveTokens(
                  idToken,
                  existingTokens?.refreshToken || '', // Preserve existing refreshToken if available
                  userId
                );
              } catch (e) {
                console.warn('Could not store tokens:', e);
              }
              
              // Use Firestore REST API to fetch/create user document
              const projectId = 'waka-running';
              const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${userId}`;
              
              debugLog('Fetching user document via Firestore REST API...');
              const firestoreResponse = await fetch(firestoreUrl, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${idToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (firestoreResponse.ok) {
                const firestoreData = await firestoreResponse.json();
                
                if (firestoreData.fields) {
                  // User document exists - parse it
                  const userData: any = {};
                  for (const [key, value] of Object.entries(firestoreData.fields)) {
                    const field = value as any;
                    let fieldValue: any = null;
                    
                    if (field.stringValue !== undefined) {
                      fieldValue = field.stringValue;
                    } else if (field.integerValue !== undefined) {
                      fieldValue = parseInt(field.integerValue);
                    } else if (field.booleanValue !== undefined) {
                      fieldValue = field.booleanValue;
                    } else if (field.timestampValue !== undefined) {
                      fieldValue = Timestamp.fromDate(new Date(field.timestampValue));
                    }
                    
                    userData[key] = fieldValue;
                  }
                  
                  const user: User = {
                    id: userId,
                    email: userData.email || result.user.email || '',
                    displayName: userData.displayName || result.user.displayName || 'User',
                    role: (userData.role || 'athlete') as 'coach' | 'athlete' | 'headCoach',
                    teamId: userData.teamId || undefined,
                    createdAt: userData.createdAt || Timestamp.now(),
                    updatedAt: userData.updatedAt || Timestamp.now(),
                    profilePrivacy: userData.profilePrivacy,
                    fcmToken: userData.fcmToken,
                    notificationsEnabled: userData.notificationsEnabled,
                    useAthleteWorkflow: userData.useAthleteWorkflow,
                    activityChartVariant: userData.activityChartVariant,
                    activityChartVariants: userData.activityChartVariants,
                    weekStartsOn: userData.weekStartsOn,
                  };
                  
                  debugLog('Manually setting user data from REST API:', user);
                  setUser(user);
                  setNeedsRoleSelection(userData.needsRoleSelection === true);
                  setProgress(100);
                  setLoading(false);
                  return;
                } else {
                  // User document doesn't exist - create it
                  debugLog('Creating new user document for Google redirect user via REST API');
                  const userData: User = {
                    id: userId,
                    email: result.user.email!,
                    displayName: result.user.displayName || 'User',
                    role: 'athlete',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                  };
                  
                  // Create user document via REST API
                  const createResponse = await fetch(firestoreUrl, {
                    method: 'PATCH',
                    headers: {
                      'Authorization': `Bearer ${idToken}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      fields: {
                        email: { stringValue: userData.email },
                        displayName: { stringValue: userData.displayName },
                        role: { stringValue: userData.role },
                        needsRoleSelection: { booleanValue: true },
                        createdAt: { timestampValue: new Date().toISOString() },
                        updatedAt: { timestampValue: new Date().toISOString() }
                      }
                    })
                  });
                  
                  if (createResponse.ok) {
                    debugLog('User document created via REST API');
                    // CRITICAL: Ensure id field is set
                    const userWithId: User = {
                      id: userId,  // Set id FIRST
                      ...userData,
                      needsRoleSelection: true
                    } as any;
                    debugLog('🔍 Setting user with id:', userWithId.id);
                    setUser(userWithId);
                    setNeedsRoleSelection(true);
                    setProgress(100);
                    setLoading(false);
                    return;
                  } else {
                    throw new Error('Failed to create user document');
                  }
                }
              } else {
                throw new Error('Failed to fetch user document');
              }
            } catch (restError: any) {
              console.error('REST API workaround failed for Google redirect:', restError);
              // Fall through to normal SDK flow
            }
          }
          
          // Normal flow for web/Android or if REST API workaround failed
          // Check if user document exists, if not create one
          const userDoc = await getDoc(doc(db, 'users', result.user.uid));
          
          if (!userDoc.exists()) {
            debugLog('Creating new user document for Google redirect user');
            setProgress(50);
            const userData: User = {
              id: result.user.uid,
              email: result.user.email!,
              displayName: result.user.displayName || 'User',
              role: 'athlete',
              createdAt: new Date() as any,
              updatedAt: new Date() as any,
            };
            
            await setDoc(doc(db, 'users', result.user.uid), {
              ...userData,
              needsRoleSelection: true,
            });
            
            debugLog('User document created with needsRoleSelection: true');
            setProgress(60);
          } else {
            setProgress(60);
          }
          
          // Manually trigger auth state change
          await handleAuthStateChange(result.user);
          return;
        } else {
          setProgress(30);
        }
      } catch (error) {
        const platform = Capacitor.getPlatform();
        console.error(`Redirect result error (non-critical, platform: ${platform}):`, error);
        
        // On iOS and Android, getRedirectResult might not work even after redirect
        // Check auth.currentUser directly as fallback
        if (platform === 'ios' || platform === 'android') {
          debugLog(`${platform}: getRedirectResult failed, checking auth.currentUser directly...`);
          try {
            // Wait a bit for redirect to process (longer for Android)
            const waitTime = platform === 'android' ? 3000 : 2000;
            await new Promise(resolve => setTimeout(resolve, waitTime));
            
            const currentUser = auth.currentUser;
            if (currentUser) {
              debugLog(`${platform}: Found user via auth.currentUser after redirect:`, currentUser.uid);
              setProgress(50);
              
              // Get idToken and use REST API workaround
              try {
                const idToken = await currentUser.getIdToken();
                const userId = currentUser.uid;
                
                if (idToken) {
                  // Store tokens
                  try {
                    localStorage.setItem('firebase_idToken', idToken);
                    localStorage.setItem('firebase_userId', userId);
                  } catch (e) {
                    console.warn('Could not store tokens:', e);
                  }
                  
                  // Use Firestore REST API to fetch/create user document
                  const projectId = 'waka-running';
                  const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${userId}`;
                  
                  debugLog(`${platform}: Fetching user document via Firestore REST API after redirect...`);
                  const firestoreResponse = await fetch(firestoreUrl, {
                    method: 'GET',
                    headers: {
                      'Authorization': `Bearer ${idToken}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  
                  if (firestoreResponse.ok) {
                    const firestoreData = await firestoreResponse.json();
                    
                    if (firestoreData.fields) {
                      // User document exists - parse it
                      const userData: any = {};
                      for (const [key, value] of Object.entries(firestoreData.fields)) {
                        const field = value as any;
                        let fieldValue: any = null;
                        
                        if (field.stringValue !== undefined) {
                          fieldValue = field.stringValue;
                        } else if (field.integerValue !== undefined) {
                          fieldValue = parseInt(field.integerValue);
                        } else if (field.booleanValue !== undefined) {
                          fieldValue = field.booleanValue;
                        } else if (field.timestampValue !== undefined) {
                          fieldValue = Timestamp.fromDate(new Date(field.timestampValue));
                        }
                        
                        userData[key] = fieldValue;
                      }
                      
                      const user: User = {
                        id: userId,
                        email: userData.email || currentUser.email || '',
                        displayName: userData.displayName || currentUser.displayName || 'User',
                        role: (userData.role || 'athlete') as 'coach' | 'athlete' | 'headCoach',
                        teamId: userData.teamId || undefined,
                        createdAt: userData.createdAt || Timestamp.now(),
                        updatedAt: userData.updatedAt || Timestamp.now(),
                        profilePrivacy: userData.profilePrivacy,
                        fcmToken: userData.fcmToken,
                        notificationsEnabled: userData.notificationsEnabled,
                        useAthleteWorkflow: userData.useAthleteWorkflow,
                        activityChartVariant: userData.activityChartVariant,
                        activityChartVariants: userData.activityChartVariants,
                        weekStartsOn: userData.weekStartsOn,
                      };
                      
                      debugLog(`${platform}: Manually setting user data from REST API after redirect:`, user);
                      setUser(user);
                      setNeedsRoleSelection(userData.needsRoleSelection === true);
                      setProgress(100);
                      setLoading(false);
                      return;
                    } else {
                      // User document doesn't exist - create it
                      debugLog(`${platform}: Creating new user document for Google redirect user via REST API`);
                      const userData: User = {
                        id: userId,
                        email: currentUser.email!,
                        displayName: currentUser.displayName || 'User',
                        role: 'athlete',
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now(),
                      };
                      
                      // Create user document via REST API
                      const createResponse = await fetch(firestoreUrl, {
                        method: 'PATCH',
                        headers: {
                          'Authorization': `Bearer ${idToken}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          fields: {
                            email: { stringValue: userData.email },
                            displayName: { stringValue: userData.displayName },
                            role: { stringValue: userData.role },
                            needsRoleSelection: { booleanValue: true },
                            createdAt: { timestampValue: new Date().toISOString() },
                            updatedAt: { timestampValue: new Date().toISOString() }
                          }
                        })
                      });
                      
                      if (createResponse.ok) {
                        debugLog(`${platform}: User document created via REST API after redirect`);
                        // CRITICAL: Ensure id field is set
                        const userWithId: User = {
                          id: userId,  // Set id FIRST
                          ...userData,
                          needsRoleSelection: true
                        } as any;
                        debugLog('🔍 Setting user with id:', userWithId.id);
                        setUser(userWithId);
                        setNeedsRoleSelection(true);
                        setProgress(100);
                        setLoading(false);
                        return;
                      }
                    }
                  } else {
                    throw new Error('Failed to fetch user document');
                  }
                } // Close if (idToken)
              } catch (restError: any) {
                console.error(`${platform}: REST API workaround failed after redirect:`, restError);
              }
            } else {
              debugLog(`${platform}: No user found in auth.currentUser after redirect`);
            }
          } catch (fallbackError) {
            console.error('iOS: Fallback check failed:', fallbackError);
          }
        }
        
        // Don't block on redirect check - continue with auth state listener
        setProgress(30);
      }
    };
    
    // Don't await - let it run in background, auth state listener will handle auth
    checkRedirectResult();
    
    // On Android, check for redirect result when URL changes to redirect handler page
    if (Capacitor.getPlatform() === 'android') {
      debugLog('Android: Setting up URL monitoring for redirect handler');
      
      const checkRedirectOnUrlChange = async () => {
        try {
          const currentUrl = window.location.href;
          debugLog('Android: Current URL:', currentUrl);
          
          if (currentUrl.includes('/__/auth/handler') || currentUrl.includes('firebaseapp.com/__/auth')) {
            debugLog('Android: Detected redirect handler URL - checking for redirect result');
            
            // Wait a moment for redirect to process
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Check for redirect result
            try {
              const result = await getRedirectResult(auth);
              if (result && result.user) {
                debugLog('✅ Android: Redirect result found:', result.user.uid);
                await handleAuthStateChange(result.user);
                // Navigate back to app root after processing redirect
                if (window.location.href.includes('firebaseapp.com')) {
                  window.location.href = '/';
                }
                return;
              }
            } catch (redirectError) {
              debugLog('Android: getRedirectResult failed, checking auth.currentUser:', redirectError);
            }
            
            // Fallback: Check auth.currentUser
            const currentUser = auth.currentUser;
            if (currentUser) {
              debugLog('Android: Found user via auth.currentUser after redirect:', currentUser.uid);
              await handleAuthStateChange(currentUser);
              // Navigate back to app root after processing redirect
              if (window.location.href.includes('firebaseapp.com')) {
                window.location.href = '/';
              }
            } else {
              debugLog('Android: No user found after redirect');
            }
          }
        } catch (error) {
          console.error('Android: Error checking redirect result:', error);
        }
      };
      
      // Check immediately if already on redirect handler page
      checkRedirectOnUrlChange();
      
      // Also check periodically (in case URL change event doesn't fire)
      const checkInterval = setInterval(() => {
        const currentUrl = window.location.href;
        if (currentUrl.includes('/__/auth/handler') || currentUrl.includes('firebaseapp.com/__/auth')) {
          debugLog('Android: Periodic check - on redirect handler page');
          checkRedirectOnUrlChange();
        }
      }, 2000); // Check every 2 seconds
      
      // Clean up interval after 30 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        debugLog('Android: Stopped periodic redirect check');
      }, 30000);
    }
    
    // Fallback: If auth state doesn't change within 5 seconds, continue anyway
    // Only set up fallback if session wasn't restored
    let fallbackTimeout: NodeJS.Timeout | undefined;
    
    // Auth state change handler (extracted to function so we can call it manually)
    const handleAuthStateChange = async (firebaseUser: any) => {
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout); // Cancel fallback since auth state changed
      }
      clearTimeout(fallbackTimeout); // Cancel fallback since auth state changed
      debugLog('Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
      
      // On iOS, if we've restored from tokens and auth.currentUser is null,
      // don't clear the user - this is the known Firebase SDK bug
      const platform = Capacitor.getPlatform();
      if (!firebaseUser && platform === 'ios' && sessionRestored && user) {
        debugLog('[AuthStateChange] iOS: auth.currentUser is null but we have restored session - keeping user logged in');
        // Don't clear the user - we've already restored from tokens
        setProgress(100);
        setLoading(false);
        return;
      }
      
      setProgress(70);
      
      try {
        if (firebaseUser) {
          debugLog('Firebase user:', firebaseUser.uid);
          setProgress(75);
          
          // Helper function to get doc with timeout
          const getDocWithTimeout = async (docRef: any, timeoutMs: number = 3000) => {
            const getDocPromise = getDoc(docRef);
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('getDoc timed out')), timeoutMs);
            });
            return await Promise.race([getDocPromise, timeoutPromise]);
          };
          
          // Get user data from Firestore with retry mechanism for new users
          let userDoc: any;
          try {
            userDoc = await getDocWithTimeout(doc(db, 'users', firebaseUser.uid));
            debugLog('User document exists:', userDoc.exists());
          } catch (error) {
            console.warn('First getDoc timed out or failed:', error);
            userDoc = { exists: () => false };
          }
          setProgress(85);
          
          // If document doesn't exist, wait a bit longer and try again (for new users on iOS)
          if (!userDoc.exists()) {
            debugLog('User document not found, waiting 2 seconds for document creation...');
            setProgress(88);
            await new Promise(resolve => setTimeout(resolve, 2000)); // Increased from 500ms to 2000ms
            
            try {
              userDoc = await getDocWithTimeout(doc(db, 'users', firebaseUser.uid), 5000); // 5 second timeout for retry
              debugLog('User document exists after retry:', userDoc.exists());
            } catch (error) {
              console.warn('Retry getDoc timed out or failed:', error);
              userDoc = { exists: () => false };
            }
            setProgress(92);
          }
          
          if (userDoc.exists()) {
            const userData = userDoc.data() as User;
            // CRITICAL: Always use firebaseUser.uid as the id - it's the source of truth
            // userDoc.id should match, but firebaseUser.uid is guaranteed to be available
            const userId = firebaseUser.uid;
            debugLog('🔍 Setting user ID - firebaseUser.uid:', userId, 'userDoc.id:', userDoc.id);
            
            // Create user object with explicit id field (don't rely on spread to preserve it)
            const userWithId: User = {
              id: userId,  // Set id FIRST to ensure it's not overwritten
              email: userData.email || firebaseUser.email || '',
              displayName: userData.displayName || firebaseUser.displayName || '',
              role: userData.role || 'athlete',
              teamId: userData.teamId,
              profilePrivacy: userData.profilePrivacy,
              fcmToken: userData.fcmToken,
              notificationsEnabled: userData.notificationsEnabled,
              useAthleteWorkflow: userData.useAthleteWorkflow,
              activityChartVariant: userData.activityChartVariant,
              activityChartVariants: userData.activityChartVariants,
              weekStartsOn: userData.weekStartsOn,
              createdAt: userData.createdAt,
              updatedAt: userData.updatedAt,
            };
            // CRITICAL: Verify id is set before setting user
            if (!userWithId.id || userWithId.id === undefined || userWithId.id === null || userWithId.id === '') {
              console.error('🔴 CRITICAL ERROR: userWithId.id is missing! firebaseUser.uid:', firebaseUser.uid, 'userId:', userId);
              userWithId.id = firebaseUser.uid; // Force set it
            }
            
            // Double-check: Verify the id is actually in the object
            debugLog('🔍 userWithId object keys:', Object.keys(userWithId));
            debugLog('🔍 userWithId.id directly accessed:', userWithId.id);
            debugLog('🔍 userWithId.id type:', typeof userWithId.id);
            debugLog('🔍 userWithId.id === undefined?', userWithId.id === undefined);
            debugLog('🔍 userWithId.id === null?', userWithId.id === null);
            debugLog('User data:', userWithId);
            debugLog('🔍 User ID in userWithId:', userWithId.id, '(type:', typeof userWithId.id, ')');
            debugLog('🔍 Full userWithId object:', JSON.stringify(userWithId, null, 2));
            debugLog('🔍🔍🔍 User role from Firestore:', userWithId.role, '(type:', typeof userWithId.role, ')');
            debugLog('🔍🔍🔍 Role comparison - === "headCoach":', userWithId.role === 'headCoach');
            debugLog('🔍🔍🔍 Role comparison - === "coach":', userWithId.role === 'coach');
            
            // Final verification before setting
            if (!userWithId.id) {
              console.error('🔴🔴🔴 CRITICAL: userWithId.id is STILL missing after all checks! Forcing to firebaseUser.uid');
              (userWithId as any).id = firebaseUser.uid;
            }
            
            setUser(userWithId);
            
            // Verify after setting (async check)
            setTimeout(() => {
              debugLog('🔍 Post-setUser verification - checking if user state has id...');
            }, 100);
            setProgress(95);
            
            // Check if this user needs role selection
            const needsRoleSelection = (userData as any).needsRoleSelection === true;
            setNeedsRoleSelection(needsRoleSelection);
            setProgress(100);
          } else {
            debugLog('No user document found after retry, user will need to register');
            setUser(null);
            setNeedsRoleSelection(false);
            setProgress(100);
          }
        } else {
          debugLog('No Firebase user');
          // Only clear user if we haven't restored from tokens
          // On iOS, auth.currentUser is often null even when user is logged in
          if (!sessionRestored || Capacitor.getPlatform() !== 'ios') {
            setUser(null);
            setNeedsRoleSelection(false);
          } else {
            debugLog('[AuthStateChange] iOS: Keeping user logged in despite null auth.currentUser (session was restored from tokens)');
          }
          setProgress(100);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setProgress(100);
      } finally {
        clearTimeout(fallbackTimeout); // Cancel fallback
        debugLog('Setting loading to false');
        // Small delay to show 100% before hiding
        setTimeout(() => {
          setLoading(false);
        }, 200);
      }
    };
    
    // Manual check of auth state immediately (in case listener doesn't fire on iOS)
    const checkAuthStateManually = async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          debugLog('Manual auth check: User found:', currentUser.uid);
          // Manually trigger the auth state handler
          await handleAuthStateChange(currentUser);
        } else {
          debugLog('Manual auth check: No user found');
          // On iOS, if we've restored from tokens, don't clear user
          const platform = Capacitor.getPlatform();
          if (platform === 'ios' && sessionRestored && user) {
            debugLog('[ManualAuthCheck] iOS: Keeping user logged in (session restored from tokens)');
            return;
          }
        }
      } catch (error) {
        console.error('Manual auth check error:', error);
      }
    };
    
    // Check immediately and after a short delay (in case Firebase is still initializing)
    // But only if session wasn't restored (to avoid race condition)
    if (!sessionRestored) {
      checkAuthStateManually();
      setTimeout(checkAuthStateManually, 1000);
    }
    
    // Set up fallback timeout (only if session wasn't restored)
    if (!sessionRestored) {
      fallbackTimeout = setTimeout(() => {
        debugLog('Auth state listener fallback: Proceeding without auth state change');
        // Do one final manual check before giving up
        checkAuthStateManually();
        if (loading) {
          setProgress(100);
          setLoading(false);
          // On iOS, if we have tokens, don't clear user
          const platform = Capacitor.getPlatform();
          if (platform === 'ios' && user) {
            debugLog('[FallbackTimeout] iOS: Keeping user logged in (may have tokens)');
          } else if (!user) {
            setUser(null);
          }
        }
      }, 5000);
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // On iOS, if we've already restored from tokens and auth.currentUser is null,
      // don't let the listener clear the user
      const platform = Capacitor.getPlatform();
      if (!firebaseUser && platform === 'ios' && sessionRestored && user) {
        debugLog('[onAuthStateChanged] iOS: Ignoring null firebaseUser - session was restored from tokens');
        return;
      }
      await handleAuthStateChange(firebaseUser);
    });

      return () => {
        clearTimeout(fallbackTimeout);
        unsubscribe();
      };
    };
    
    // Call the async initialization function
    let cleanup: (() => void) | undefined;
    initializeAuth().then((cleanupFn) => {
      cleanup = cleanupFn;
    }).catch((error) => {
      console.error('Error initializing auth:', error);
    });
    
    // Return cleanup function
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    setProgress(0);
    
    try {
      setProgress(10);
      
      // Check if auth is ready
      if (!auth) {
        throw new Error('Firebase authentication is not initialized. Please refresh the app.');
      }
      
      setProgress(20);
      debugLog('=== AUTH CONTEXT: Starting sign-in ===');
      debugLog('Attempting email/password sign-in...');
      
      const platform = Capacitor.getPlatform();
      
      // SDK does not work - use REST API workaround for all platforms
      // Use REST API directly as workaround, bypassing SDK entirely
      if (platform === 'ios' || platform === 'web' || platform === 'android') {
        debugLog(`${platform} platform detected - using REST API workaround (SDK does not work)`);
        setProgress(30);
        
        try {
          // Use Firebase REST API directly
          const apiKey = "AIzaSyBq1eloUDVA2XfSlLuUCOdDVqdEoDeYfl4";
          const restUrl = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`;
          
          debugLog('Calling Firebase REST API directly...');
          const restResponse = await fetch(restUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email,
              password: password,
              returnSecureToken: true
            })
          });
          
          if (!restResponse.ok) {
            const errorData = await restResponse.json();
            console.error('REST API error:', errorData);
            throw new Error(errorData.error?.message || 'Sign-in failed');
          }
          
          const authData = await restResponse.json();
          debugLog('REST API success! User ID:', authData.localId);
          debugLog('Got idToken:', authData.idToken ? 'yes' : 'no');
          
          // REST API succeeded - we have valid tokens but SDK won't process them
          // Workaround: Manually fetch user data and set state, bypassing auth.currentUser
          // SKIP SDK ATTEMPT - SDK does not work, use REST API workaround directly
          debugLog('REST API succeeded, using REST API workaround directly (SDK does not work)...');
          setProgress(60);
          
          // Store tokens for future use (persistent storage)
          if (authData.idToken) {
            try {
              await saveTokens(
                authData.idToken,
                authData.refreshToken || '',
                authData.localId
              );
            } catch (e) {
              console.warn('Could not store tokens:', e);
            }
          }
          
          // Use REST API workaround directly - SDK does not work
          debugLog('Using manual workaround: fetching user data via Firestore REST API...');
            
            // Manual workaround: Use Firestore REST API with idToken
            // This bypasses the SDK's requirement for auth.currentUser
            setProgress(70);
            
            try {
              const userId = authData.localId;
              const idToken = authData.idToken;
              
              if (!idToken) {
                throw new Error('No idToken received from REST API');
              }
              
              // Use Firestore REST API to fetch user document
              // This works even when auth.currentUser is not set
              const projectId = 'waka-running';
              const firestoreUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/users/${userId}`;
              
              debugLog('Fetching user document via Firestore REST API...');
              const firestoreResponse = await fetch(firestoreUrl, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${idToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (!firestoreResponse.ok) {
                const errorData = await firestoreResponse.json().catch(() => ({}));
                console.error('Firestore REST API error:', errorData);
                throw new Error(`Firestore REST API failed: ${firestoreResponse.status} ${firestoreResponse.statusText}`);
              }
              
              const firestoreData = await firestoreResponse.json();
              debugLog('Firestore REST API success! Document exists:', !!firestoreData.fields);
              debugLog('Raw Firestore response fields:', JSON.stringify(firestoreData.fields, null, 2));
              
              if (firestoreData.fields) {
                // Convert Firestore REST API format to our User type
                const userData: any = {};
                for (const [key, value] of Object.entries(firestoreData.fields)) {
                  const field = value as any;
                  let fieldValue: any = null;
                  
                  // Firestore REST API uses valueType pattern
                  if (field.stringValue !== undefined) {
                    fieldValue = field.stringValue;
                  } else if (field.integerValue !== undefined) {
                    fieldValue = parseInt(field.integerValue);
                  } else if (field.doubleValue !== undefined) {
                    fieldValue = parseFloat(field.doubleValue);
                  } else if (field.booleanValue !== undefined) {
                    fieldValue = field.booleanValue;
                  } else if (field.timestampValue !== undefined) {
                    // Timestamp format: "2025-09-24T01:41:50.222Z" - convert to Firestore Timestamp
                    const date = new Date(field.timestampValue);
                    fieldValue = Timestamp.fromDate(date);
                  } else if (field.nullValue !== undefined) {
                    fieldValue = null;
                  } else {
                    console.warn(`Unknown field type for ${key}:`, field);
                    fieldValue = null;
                  }
                  
                  userData[key] = fieldValue;
                  debugLog(`Parsed field ${key}:`, fieldValue);
                }
                
                debugLog('Parsed userData:', userData);
                
                // Convert to User type
                // CRITICAL: Always use userId (from authData.localId) as the id - it's the source of truth
                const user: User = {
                  id: userId,  // Set id FIRST to ensure it's not overwritten
                  email: userData.email || authData.email || '',
                  displayName: userData.displayName || authData.displayName || '',
                  role: (userData.role || 'athlete') as 'coach' | 'athlete' | 'headCoach',
                  teamId: userData.teamId || undefined, // Use undefined instead of empty string
                  createdAt: userData.createdAt || Timestamp.now(),
                  updatedAt: userData.updatedAt || Timestamp.now(),
                  profilePrivacy: userData.profilePrivacy,
                  fcmToken: userData.fcmToken,
                  notificationsEnabled: userData.notificationsEnabled,
                  useAthleteWorkflow: userData.useAthleteWorkflow,
                  activityChartVariant: userData.activityChartVariant,
                  activityChartVariants: userData.activityChartVariants,
                  weekStartsOn: userData.weekStartsOn,
                };
                
                // CRITICAL: Verify id is set before setting user
                if (!user.id || user.id === undefined || user.id === null) {
                  console.error('🔴 CRITICAL ERROR: user.id is missing! userId:', userId);
                  user.id = userId; // Force set it
                }
                
                debugLog('Manually setting user data from REST API:', user);
                debugLog('🔍 User ID in user object:', user.id, '(type:', typeof user.id, ')');
                debugLog('🔍 Full user object:', JSON.stringify(user, null, 2));
                debugLog('teamId value:', user.teamId, 'type:', typeof user.teamId);
                
                // If teamId is missing, try to look it up via teamMembers collection
                if (!user.teamId && user.role === 'athlete') {
                  debugLog('teamId is missing, looking up via teamMembers collection...');
                  setProgress(85);
                  
                  try {
                    // Use Firestore REST API to query teamMembers
                    // Firestore REST API uses POST for queries with structuredQuery
                    const projectId = 'waka-running';
                    const teamMembersUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents:runQuery`;
                    
                    const queryBody = {
                      structuredQuery: {
                        from: [{ collectionId: 'teamMembers' }],
                        where: {
                          fieldFilter: {
                            field: { fieldPath: 'athleteId' },
                            op: 'EQUAL',
                            value: { stringValue: userId }
                          }
                        },
                        limit: 1
                      }
                    };
                    
                    const teamMembersResponse = await fetch(teamMembersUrl, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(queryBody)
                    });
                    
                    if (teamMembersResponse.ok) {
                      const teamMembersData = await teamMembersResponse.json();
                      debugLog('teamMembers query result:', teamMembersData);
                      
                      if (teamMembersData && teamMembersData.length > 0) {
                        // Firestore REST API runQuery returns array of documents
                        const firstResult = teamMembersData[0];
                        const document = firstResult.document;
                        if (document && document.fields) {
                          const teamIdField = document.fields.teamId;
                          if (teamIdField?.stringValue) {
                            const foundTeamId = teamIdField.stringValue;
                            debugLog('Found teamId via teamMembers:', foundTeamId);
                            user.teamId = foundTeamId;
                          }
                        }
                      } else {
                        debugLog('No team memberships found for this athlete');
                      }
                    } else {
                      const errorText = await teamMembersResponse.text();
                      console.warn('Failed to query teamMembers:', teamMembersResponse.status, errorText);
                    }
                  } catch (teamLookupError: any) {
                    console.warn('Error looking up team via teamMembers:', teamLookupError);
                    // Continue anyway - teamId will remain undefined
                  }
                }
                
                debugLog('Final user object with teamId:', user.teamId);
                setUser(user);
                const needsRoleSelection = (userData.needsRoleSelection === true);
                setNeedsRoleSelection(needsRoleSelection);
                setProgress(100);
                setLoading(false);
                return; // Success with REST API workaround!
              } else {
                throw new Error('User document not found in Firestore');
              }
            } catch (manualError: any) {
              console.error('Manual REST API workaround failed:', manualError);
              console.error('Error details:', {
                message: manualError?.message,
                stack: manualError?.stack
              });
              // Fall through to show error
              throw new Error('iOS Sign-In Issue: Unable to fetch user data. Firebase SDK on iOS is not processing authentication responses. This is a known limitation. Please use the web version at www.streakerbymx.com, or contact support for updates on when this will be fixed.');
            }
          
        } catch (restError: any) {
          console.error('REST API approach failed:', restError);
          // Fall through to normal SDK approach
          debugLog('Falling back to normal SDK sign-in...');
        }
      }
      
      // Normal SDK approach (for web/Android, or iOS fallback)
      debugLog('Using Firebase SDK signInWithEmailAndPassword...');
      const signInPromise = signInWithEmailAndPassword(auth, email, password);
      
      // Track promise state for debugging
      let promiseResolved = false;
      let promiseRejected = false;
      let promiseResult: any = null;
      let promiseError: any = null;
      
      signInPromise.then((res) => {
        promiseResolved = true;
        promiseResult = res;
        debugLog('Sign-in promise RESOLVED! User:', res.user?.uid);
      }).catch((err) => {
        promiseRejected = true;
        promiseError = err;
        console.error('Sign-in promise REJECTED:', err);
      });
      
      setProgress(40);
      
      // Wait for network request to complete (we know it takes ~134ms)
      debugLog('Waiting for network request to complete (~300ms)...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Poll auth.currentUser with retries
      debugLog('Polling auth.currentUser...');
      const maxPollAttempts = 50; // 50 * 200ms = 10 seconds
      let pollAttempts = 0;
      let currentUser = null;
      
      while (pollAttempts < maxPollAttempts && !currentUser) {
        if (promiseResolved && promiseResult) {
          debugLog('Promise resolved during polling! User:', promiseResult.user?.uid);
          currentUser = promiseResult.user;
          break;
        }
        
        if (promiseRejected && promiseError) {
          console.error('Promise rejected during polling! Error:', promiseError);
          throw promiseError;
        }
        
        currentUser = auth.currentUser;
        pollAttempts++;
        
        const progressPercent = Math.min(40 + Math.floor((pollAttempts / maxPollAttempts) * 50), 90);
        setProgress(progressPercent);
        
        if (currentUser) {
          debugLog('auth.currentUser is set! User:', currentUser.uid);
          break;
        }
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (pollAttempts % 10 === 0) {
          debugLog(`Still polling... (attempt ${pollAttempts}/${maxPollAttempts})`);
        }
      }
      
      // Check final state
      if (!currentUser) {
        if (promiseResolved && promiseResult) {
          currentUser = promiseResult.user;
        } else if (promiseRejected && promiseError) {
          throw promiseError;
        } else {
          console.error('Polling timeout after', maxPollAttempts, 'attempts');
          throw new Error('Sign-in timed out. Please check your internet connection and try again.');
        }
      }
      
      const result = { user: currentUser };
      
      debugLog('Sign-in successful! User:', result.user.uid);
      setProgress(90);
      
      // Since auth state listener may not fire on iOS, manually handle auth state
      // Get user data from Firestore
      try {
        let userDoc = await getDoc(doc(db, 'users', result.user.uid));
        debugLog('User document exists after sign-in:', userDoc.exists());
        
        if (!userDoc.exists()) {
          // Wait a bit and retry (for new users)
          await new Promise(resolve => setTimeout(resolve, 500));
          userDoc = await getDoc(doc(db, 'users', result.user.uid));
        }
        
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          // CRITICAL: Always use result.user.uid as the id - it's the source of truth
          const userId = result.user.uid;  // Use result.user.uid, not currentUser.uid
          debugLog('🔍 Setting user ID after sign-in (SDK) - result.user.uid:', userId, 'currentUser.uid:', currentUser.uid, 'userDoc.id:', userDoc.id);
          
          // Create user object with explicit id field (don't rely on spread to preserve it)
          const userWithId: User = {
            id: userId,  // Set id FIRST to ensure it's not overwritten
            email: userData.email || result.user.email || '',
            displayName: userData.displayName || result.user.displayName || '',
            role: userData.role || 'athlete',
            teamId: userData.teamId,
            profilePrivacy: userData.profilePrivacy,
            fcmToken: userData.fcmToken,
            notificationsEnabled: userData.notificationsEnabled,
            useAthleteWorkflow: userData.useAthleteWorkflow,
            activityChartVariant: userData.activityChartVariant,
            activityChartVariants: userData.activityChartVariants,
            weekStartsOn: userData.weekStartsOn,
            createdAt: userData.createdAt,
            updatedAt: userData.updatedAt,
          };
          debugLog('Setting user data after sign-in:', userWithId);
          debugLog('🔍 User ID in userWithId:', userWithId.id, '(type:', typeof userWithId.id, ')');
          debugLog('🔍 Full userWithId object:', JSON.stringify(userWithId, null, 2));
          
          // CRITICAL: Verify id is set before setting user
          if (!userWithId.id || userWithId.id === undefined || userWithId.id === null) {
            console.error('🔴 CRITICAL ERROR: userWithId.id is missing! result.user.uid:', result.user.uid);
            userWithId.id = result.user.uid; // Force set it
          }
          
          setUser(userWithId);
          const needsRoleSelection = (userData as any).needsRoleSelection === true;
          setNeedsRoleSelection(needsRoleSelection);
          setProgress(100);
        } else {
          debugLog('No user document found after sign-in');
          setUser(null);
          setNeedsRoleSelection(false);
          setProgress(100);
        }
      } catch (error) {
        console.error('Error getting user data after sign-in:', error);
        setProgress(100);
      }
      
      setLoading(false);
    } catch (error: any) {
      // Log error in multiple ways for Capacitor compatibility
      console.error('AuthContext: Sign-in error caught:', error);
      console.error('AuthContext: Error type:', typeof error);
      console.error('AuthContext: Error string:', String(error));
      
      // Log individual properties separately so Capacitor can capture them
      if (error) {
        console.error('AuthContext: error.message:', error?.message || 'NO MESSAGE');
        console.error('AuthContext: error.code:', error?.code || 'NO CODE');
        console.error('AuthContext: error.name:', error?.name || 'NO NAME');
        console.error('AuthContext: error.toString():', error?.toString() || 'NO STRING');
        
        // Try to get Firebase error code
        if (error?.code) {
          console.error('AuthContext: Firebase error code:', error.code);
        }
        if (error?.message) {
          console.error('AuthContext: Firebase error message:', error.message);
        }
      }
      
      setLoading(false);
      setProgress(0);
      
      // Provide more helpful error messages
      const errorMessage = error?.message || error?.toString() || 'Failed to sign in';
      console.error('AuthContext: Final error message:', errorMessage);
      
      // Ensure we always throw a proper error
      let finalError: Error;
      if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        finalError = new Error('Sign-in timed out. Please check your internet connection and try again.');
      } else if (error?.code === 'auth/invalid-credential' || error?.code === 'auth/user-not-found') {
        finalError = new Error('Invalid email or password. Please try again.');
      } else if (error?.code === 'auth/network-request-failed') {
        finalError = new Error('Network error. Please check your internet connection and try again.');
      } else {
        finalError = new Error(errorMessage || 'Failed to sign in. Please try again.');
      }
      
      console.error('Throwing final error:', finalError.message);
      throw finalError;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    role: 'coach' | 'athlete' | 'headCoach'
  ) => {
    setLoading(true);
    setProgress(0);
    debugLog('[SignUp] Starting signup process...');
    const platform = Capacitor.getPlatform();

    try {
      setProgress(20);
      
      // On iOS, use REST API for entire signup flow
      if (platform === 'ios') {
        debugLog('[SignUp] iOS detected - using REST API for entire signup flow');
        
        // Step 1: Create user with REST API
        debugLog('[SignUp] Creating Firebase Auth user via REST API...');
        const signUpResponse = await signUpWithEmailAndPasswordRest(email, password);
        debugLog('[SignUp] User created via REST API:', signUpResponse.localId);
        setProgress(40);
        
        // Step 2: Update display name with REST API
        debugLog('[SignUp] Updating display name via REST API...');
        await updateProfileRest(signUpResponse.idToken, displayName);
        debugLog('[SignUp] Display name updated via REST API');
        setProgress(60);
        
        // Step 3: Store tokens for Firestore REST API
        try {
          await saveTokens(
            signUpResponse.idToken,
            signUpResponse.refreshToken || '',
            signUpResponse.localId
          );
          debugLog('[SignUp] Stored tokens');
        } catch (e) {
          console.warn('[SignUp] Could not store tokens:', e);
        }
        
        // Step 4: Create user document via REST API
        const userData: User = {
          id: signUpResponse.localId,
          email: signUpResponse.email,
          displayName,
          role,
          createdAt: new Date() as any,
          updatedAt: new Date() as any,
        };
        
        debugLog('[SignUp] Creating user document via REST API...');
        await firestoreRestApi.setDocument('users', signUpResponse.localId, userData);
        debugLog('[SignUp] User document created successfully');
        setProgress(90);
        
        // Step 5: Try to sign in with SDK to trigger onAuthStateChanged
        // Add timeout since SDK often hangs on iOS
        debugLog('[SignUp] Attempting SDK sign-in to complete authentication flow...');
        try {
          const signInPromise = signInWithEmailAndPassword(auth, email, password);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('SDK sign-in timeout')), 3000)
          );
          
          await Promise.race([signInPromise, timeoutPromise]);
          debugLog('[SignUp] SDK sign in successful');
        } catch (signInError) {
          console.warn('[SignUp] SDK sign in timed out or failed (expected on iOS):', signInError);
          // This is expected on iOS - manually set user state instead of waiting for polling
        }
        
        setProgress(100);
        debugLog('[SignUp] Signup complete! Setting user state manually...');
        
        // On iOS, auth.currentUser won't be set, so manually set user state
        // This allows the app to continue without waiting for polling
        setUser(userData);
        setNeedsRoleSelection(false); // User just selected role during signup
        setLoading(false);
        debugLog('[SignUp] User state set, app should now load!');
        
      } else {
        // Web version - use normal Firebase SDK
        debugLog('[SignUp] Web platform - using Firebase SDK');
        const firebaseUserCredential = await createUserWithEmailAndPassword(auth, email, password);
        const firebaseUser = firebaseUserCredential.user;
        debugLog('[SignUp] Firebase Auth user created:', firebaseUser.uid);
        setProgress(50);
        
        await updateProfile(firebaseUser, { displayName });
        debugLog('[SignUp] Profile updated');
        setProgress(70);
        
        const userData: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName,
          role,
          createdAt: new Date() as any,
          updatedAt: new Date() as any,
        };
        
        await setDoc(doc(db, 'users', firebaseUser.uid), userData);
        debugLog('[SignUp] User document created successfully');
        setProgress(100);
      }
      
      debugLog('[SignUp] Signup complete!');
    } catch (error: any) {
      setLoading(false);
      setProgress(0);
      console.error('[SignUp] Signup error:', error);
      throw error;
    }
  };

  // ============================================
  // GOOGLE SIGN-IN - TEMPORARILY DISABLED
  // ============================================
  // Google Sign-In is disabled due to iOS/Capacitor compatibility issues.
  // The @capacitor-firebase/authentication plugin imports but returns empty object.
  // All original code is preserved in comments below for future restoration.
  // See GOOGLE_SIGNIN_RESTORE_GUIDE.md for details and restoration steps.
  // ============================================
  
  const signInWithGoogle = async () => {
    debugLog('🟡🟡🟡 signInWithGoogle FUNCTION CALLED 🟡🟡🟡');
    debugLog('🟡🟡🟡 THIS IS THE FIRST LINE OF signInWithGoogle 🟡🟡🟡');
    
    // Force multiple log statements to ensure they're captured
    for (let i = 0; i < 5; i++) {
      debugLog(`🟡 Log ${i + 1}/5: signInWithGoogle is executing`);
    }
    
    // Check platform - disable only on iOS (native plugin not working)
    const platform = Capacitor.getPlatform();
    debugLog('🟡 Platform:', platform);
    debugLog('🟡 Platform type:', typeof platform);
    
    if (platform === 'ios') {
      throw new Error('Google Sign-In is currently unavailable on iOS. Please use email/password sign-in or visit the web version at www.streakerbymx.com');
    }

    // For Android, use native plugin (more reliable than popup/redirect)
    if (platform === 'android') {
      debugLog('🔵🔵🔵 ANDROID NATIVE PLUGIN CALLED - THIS IS A TEST LOG 🔵🔵🔵');
      debugLog('🔵🔵🔵 ANDROID DETECTED - CALLING NATIVE PLUGIN 🔵🔵🔵');
      debugLog('Android: Using native Google Sign-In plugin');
      debugLog('🔵🔵🔵 IF YOU SEE THIS, THE CODE IS RUNNING 🔵🔵🔵');
      debugLog('🔵 About to call signInWithGoogleNative()...');
      return signInWithGoogleNative();
    }

    // Web platform - use Firebase web SDK popup (this works!)
    setLoading(true);
    setProgress(0);
    try {
      setProgress(10);
      const provider = new GoogleAuthProvider();
      
      // Configure the provider for better popup handling
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      setProgress(30);
      const result = await signInWithPopup(auth, provider);
      
      debugLog('Google Sign-In successful:', result.user.uid);
      setProgress(60);
      
      // CRITICAL: Create user document BEFORE auth state listener processes the user
      // This prevents the race condition where auth state listener fires before document exists
      const userData: User = {
        id: result.user.uid,
        email: result.user.email!,
        displayName: result.user.displayName || 'User',
        role: 'athlete', // Temporary default - will be updated when user selects role
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
      };
      
      // Check if user document exists, if not create one
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      setProgress(75);
      
      if (!userDoc.exists()) {
        debugLog('Creating new user document for Google user');
        await setDoc(doc(db, 'users', result.user.uid), {
          ...userData,
          needsRoleSelection: true, // Flag to indicate role selection is needed
        });
        debugLog('User document created with needsRoleSelection: true');
        setProgress(90);
      } else {
        debugLog('User document already exists');
        // Ensure the needsRoleSelection flag is set if it's missing
        const existingData = userDoc.data();
        if (!existingData.needsRoleSelection) {
          await updateDoc(doc(db, 'users', result.user.uid), {
            needsRoleSelection: true,
            updatedAt: serverTimestamp(),
          });
          debugLog('Updated existing user document with needsRoleSelection: true');
        }
        setProgress(90);
      }
      
      setProgress(100);
      // Document is now created, auth state listener will handle the rest
    } catch (error: any) {
      console.error('Google Sign-In error:', error);
      setLoading(false);
      setProgress(0);
      
      // If popup fails, try redirect as fallback
      if (error.code === 'auth/popup-closed-by-user' || 
          error.code === 'auth/popup-blocked' || 
          error.code === 'auth/cross-origin-operation') {
        debugLog('Popup failed, falling back to redirect');
        return signInWithGoogleRedirect();
      }
      
      // Provide more helpful error messages
      throw new Error(error.message || 'Failed to sign in with Google. Please try again.');
    }
    // Note: setLoading(false) is handled by the auth state listener
  };

  const signInWithGoogleNative = async () => {
    // ENABLED FOR ANDROID: Native Google Sign-In using @capacitor-firebase/authentication
    setLoading(true);
    setProgress(0);
    try {
      setProgress(20);
      debugLog('🟢🟢🟢 signInWithGoogleNative FUNCTION CALLED 🟢🟢🟢');
      debugLog('Using native Google Sign-In plugin');
      
      // Dynamically import the Firebase Authentication plugin
      let FirebaseAuthentication: any;
      try {
        const authModule = await import('@capacitor-firebase/authentication');
        FirebaseAuthentication = authModule.FirebaseAuthentication;
        debugLog('Firebase Authentication plugin imported successfully');
        debugLog('FirebaseAuthentication object:', FirebaseAuthentication);
        debugLog('FirebaseAuthentication type:', typeof FirebaseAuthentication);
        
        // Check if plugin exists
        if (!FirebaseAuthentication) {
          throw new Error('Firebase Authentication plugin is null or undefined. Plugin may not be properly installed.');
        }
        
        // Check if signInWithGoogle method exists (Proxy objects don't show keys, so check method directly)
        if (typeof FirebaseAuthentication.signInWithGoogle !== 'function') {
          console.error('FirebaseAuthentication.signInWithGoogle is not a function');
          console.error('FirebaseAuthentication type:', typeof FirebaseAuthentication);
          // Try to see what properties are available
          try {
            console.error('Available properties:', Object.getOwnPropertyNames(FirebaseAuthentication));
            console.error('FirebaseAuthentication keys:', Object.keys(FirebaseAuthentication));
          } catch (e) {
            console.error('Could not enumerate properties (Proxy object):', e);
          }
          throw new Error('FirebaseAuthentication.signInWithGoogle is not a function. Plugin may not be properly registered.');
        }
        
        debugLog('✅ FirebaseAuthentication.signInWithGoogle is a function - plugin is ready!');
      } catch (importError: any) {
        console.error('Failed to import Firebase Authentication plugin:', importError);
        throw new Error(`Firebase Authentication plugin not available: ${importError.message}. Please install @capacitor-firebase/authentication and run 'npm install', 'npx cap sync android', and 'npx cap open android' to ensure native setup.`);
      }
      
      // Use Capacitor Firebase Auth plugin for native Google Sign-In
      debugLog('Calling FirebaseAuthentication.signInWithGoogle()...');
      
      // Add timeout to catch if native plugin hangs
      const nativeSignInPromise = FirebaseAuthentication.signInWithGoogle();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Native Google Sign-In timed out after 30 seconds')), 30000);
      });
      
      let result: any;
      try {
        debugLog('Waiting for native sign-in result (with 30s timeout)...');
        result = await Promise.race([nativeSignInPromise, timeoutPromise]);
        debugLog('Native Google Sign-In result received:', result);
        debugLog('Result type:', typeof result);
        debugLog('Result keys:', result ? Object.keys(result) : 'null');
        
        if (result?.user?.uid) {
          debugLog('Native Google Sign-In successful, user ID:', result.user.uid);
        } else if (result?.user) {
          console.warn('Native Google Sign-In returned user object but no uid. User:', result.user);
        } else {
          console.warn('Native Google Sign-In returned but no user found. Result:', result);
        }
      } catch (pluginError: any) {
        console.error('FirebaseAuthentication.signInWithGoogle() error:', pluginError);
        
        // Extract error message SAFELY - Capacitor sends errors as { error: { message: "..." } }
        let errorMessage = '';
        let hasNoCredentials = false;
        
        // Try to extract message without any string conversion that might throw
        try {
          if (pluginError) {
            // Check pluginError.message (direct error)
            if (pluginError.message && typeof pluginError.message === 'string') {
              errorMessage = pluginError.message;
            }
            // Check pluginError.error.message (Capacitor wrapped error)
            else if (pluginError.error && pluginError.error.message && typeof pluginError.error.message === 'string') {
              errorMessage = pluginError.error.message;
            }
            // Check if it's already a string
            else if (typeof pluginError === 'string') {
              errorMessage = pluginError;
            }
          }
        } catch (extractError) {
          // If extraction fails, we'll check for the error in the console log
          console.error('Error extraction failed, but continuing...', extractError);
        }
        
        // Check for "No credentials available" - this is the key check
        if (errorMessage) {
          hasNoCredentials = 
            errorMessage.includes('No credentials available') || 
            errorMessage.includes('NoCredentialException') ||
            errorMessage.includes('No credential');
        }
        
        console.error('🔴 Plugin error message:', errorMessage || '[No message found]');
        console.error('🔴 Plugin error type:', typeof pluginError);
        console.error('🔴 Has no credentials:', hasNoCredentials);
        
        // If no credentials available, fallback to web-based Google Sign-In
        if (hasNoCredentials) {
          debugLog('⚠️ No Google account on device - falling back to web-based Google Sign-In');
          debugLog('This is expected on emulators without Google accounts configured');
          
          // Fallback to web-based Google Sign-In (redirect method)
          try {
            setProgress(30);
            debugLog('Attempting web-based Google Sign-In redirect as fallback...');
            await signInWithGoogleRedirect();
            // Note: setLoading(false) will be handled by redirect or auth state listener
            return; // Exit early - redirect will handle the rest
          } catch (redirectError: any) {
            console.error('Web-based redirect also failed:', redirectError);
            throw new Error('No Google account found on this device. Please add a Google account in Android Settings > Accounts, then try again. Alternatively, you can use email/password sign-in.');
          }
        }
        
        // If it's a timeout, provide helpful message
        if (errorMessage && errorMessage.includes('timed out')) {
          throw new Error('Native Google Sign-In timed out. The native plugin may not be properly configured. Please check that @capacitor-firebase/authentication is installed and npx cap sync android was run.');
        }
        
        // Generic error - use the extracted message or a safe fallback
        const finalErrorMessage = errorMessage || 'Unknown error';
        throw new Error(`Native Google Sign-In failed: ${finalErrorMessage}`);
      }
      
      setProgress(60);
      
      // The native plugin should automatically update Firebase Auth state
      // But we need to wait for auth.currentUser to be set
      // Poll for auth.currentUser to be set (similar to email/password)
      let currentUser = auth.currentUser;
      let pollAttempts = 0;
      const maxPollAttempts = 30; // 30 * 200ms = 6 seconds
      
      while (!currentUser && pollAttempts < maxPollAttempts) {
        await new Promise(resolve => setTimeout(resolve, 200));
        currentUser = auth.currentUser;
        pollAttempts++;
        
        if (pollAttempts % 10 === 0) {
          debugLog(`Polling for auth.currentUser after native sign-in... (attempt ${pollAttempts}/${maxPollAttempts})`);
        }
      }
      
      if (!currentUser) {
        // Fallback: Try to get user from result if available
        if (result?.user) {
          debugLog('Using user from plugin result instead of auth.currentUser');
          // The user from result should be a Firebase User object
          // We'll let the auth state listener handle it, or manually fetch user data
          debugLog('Note: Plugin returned user but auth.currentUser not set - this may require manual handling');
        }
        throw new Error('Native Google Sign-In completed but auth.currentUser was not set');
      }
      
      debugLog('auth.currentUser set after native sign-in:', currentUser.uid);
      setProgress(75);
      
      // Check if user document exists, if not create one
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (!userDoc.exists()) {
        debugLog('Creating new user document for native Google user');
        const userData: User = {
          id: currentUser.uid,
          email: currentUser.email!,
          displayName: currentUser.displayName || 'User',
          role: 'athlete',
          createdAt: new Date() as any,
          updatedAt: new Date() as any,
        };
        
        await setDoc(doc(db, 'users', currentUser.uid), {
          ...userData,
          needsRoleSelection: true,
        });
        debugLog('User document created with needsRoleSelection: true');
        setProgress(90);
      } else {
        debugLog('User document already exists');
        const existingData = userDoc.data();
        if (!existingData.needsRoleSelection) {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            needsRoleSelection: true,
            updatedAt: serverTimestamp(),
          });
          debugLog('Updated existing user document with needsRoleSelection: true');
        }
        setProgress(90);
      }
      
      setProgress(100);
      // Auth state listener will handle the rest
    } catch (error: any) {
      console.error('Native Google Sign-In error:', error);
      setLoading(false);
      setProgress(0);
      throw new Error(error.message || 'Failed to sign in with Google. Please try again.');
    }
    // Note: setLoading(false) is handled by the auth state listener
    
    /* ORIGINAL CODE - PRESERVED FOR FUTURE RESTORATION
    setLoading(true);
    setProgress(0);
    try {
      setProgress(20);
      debugLog('Using native Google Sign-In plugin');
      
      // Dynamically import the Firebase Authentication plugin
      let FirebaseAuthentication: any;
      try {
        const authModule = await import('@capacitor-firebase/authentication');
        FirebaseAuthentication = authModule.FirebaseAuthentication;
        debugLog('Firebase Authentication plugin imported successfully');
        debugLog('FirebaseAuthentication object:', FirebaseAuthentication);
        debugLog('FirebaseAuthentication type:', typeof FirebaseAuthentication);
        
        // Check if plugin exists
        if (!FirebaseAuthentication) {
          throw new Error('Firebase Authentication plugin is null or undefined. Plugin may not be properly installed.');
        }
        
        // Check if signInWithGoogle method exists (Proxy objects don't show keys, so check method directly)
        if (typeof FirebaseAuthentication.signInWithGoogle !== 'function') {
          console.error('FirebaseAuthentication.signInWithGoogle is not a function');
          console.error('FirebaseAuthentication type:', typeof FirebaseAuthentication);
          // Try to see what properties are available
          try {
            console.error('Available properties:', Object.getOwnPropertyNames(FirebaseAuthentication));
            console.error('FirebaseAuthentication keys:', Object.keys(FirebaseAuthentication));
          } catch (e) {
            console.error('Could not enumerate properties (Proxy object):', e);
          }
          throw new Error('FirebaseAuthentication.signInWithGoogle is not a function. Plugin may not be properly registered.');
        }
        
        debugLog('✅ FirebaseAuthentication.signInWithGoogle is a function - plugin is ready!');
      } catch (importError: any) {
        console.error('Failed to import Firebase Authentication plugin:', importError);
        throw new Error(`Firebase Authentication plugin not available: ${importError.message}. Please install @capacitor-firebase/authentication and run 'npm install', 'npx cap sync ios', and 'pod install' in ios/App directory.`);
      }
      
      // Use Capacitor Firebase Auth plugin for native Google Sign-In
      debugLog('Calling FirebaseAuthentication.signInWithGoogle()...');
      
      // Add timeout to catch if native plugin hangs
      const nativeSignInPromise = FirebaseAuthentication.signInWithGoogle();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Native Google Sign-In timed out after 30 seconds')), 30000);
      });
      
      let result: any;
      try {
        debugLog('Waiting for native sign-in result (with 30s timeout)...');
        result = await Promise.race([nativeSignInPromise, timeoutPromise]);
        debugLog('Native Google Sign-In result received:', result);
        debugLog('Result type:', typeof result);
        debugLog('Result keys:', result ? Object.keys(result) : 'null');
        
        if (result?.user?.uid) {
          debugLog('Native Google Sign-In successful, user ID:', result.user.uid);
        } else if (result?.user) {
          console.warn('Native Google Sign-In returned user object but no uid. User:', result.user);
        } else {
          console.warn('Native Google Sign-In returned but no user found. Result:', result);
        }
      } catch (pluginError: any) {
        console.error('FirebaseAuthentication.signInWithGoogle() error:', pluginError);
        console.error('Error details:', {
          message: pluginError?.message,
          code: pluginError?.code,
          name: pluginError?.name,
          toString: pluginError?.toString(),
          stack: pluginError?.stack
        });
        
        // If it's a timeout, provide helpful message
        if (pluginError?.message?.includes('timed out')) {
          throw new Error('Native Google Sign-In timed out. The native plugin may not be properly configured. Please check that @capacitor-firebase/authentication is installed and npx cap sync ios was run.');
        }
        
        throw new Error(`Native Google Sign-In failed: ${pluginError?.message || 'Unknown error'}`);
      }
      
      setProgress(60);
      
      // The native plugin should automatically update Firebase Auth state
      // But we need to wait for auth.currentUser to be set
      // Poll for auth.currentUser to be set (similar to email/password)
      let currentUser = auth.currentUser;
      let pollAttempts = 0;
      const maxPollAttempts = 30; // 30 * 200ms = 6 seconds
      
      while (!currentUser && pollAttempts < maxPollAttempts) {
        await new Promise(resolve => setTimeout(resolve, 200));
        currentUser = auth.currentUser;
        pollAttempts++;
        
        if (pollAttempts % 10 === 0) {
          debugLog(`Polling for auth.currentUser after native sign-in... (attempt ${pollAttempts}/${maxPollAttempts})`);
        }
      }
      
      if (!currentUser) {
        throw new Error('Native Google Sign-In completed but auth.currentUser was not set');
      }
      
      debugLog('auth.currentUser set after native sign-in:', currentUser.uid);
      setProgress(75);
      
      // Check if user document exists, if not create one
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (!userDoc.exists()) {
        debugLog('Creating new user document for native Google user');
        const userData: User = {
          id: currentUser.uid,
          email: currentUser.email!,
          displayName: currentUser.displayName || 'User',
          role: 'athlete',
          createdAt: new Date() as any,
          updatedAt: new Date() as any,
        };
        
        await setDoc(doc(db, 'users', currentUser.uid), {
          ...userData,
          needsRoleSelection: true,
        });
        debugLog('User document created with needsRoleSelection: true');
        setProgress(90);
      } else {
        debugLog('User document already exists');
        const existingData = userDoc.data();
        if (!existingData.needsRoleSelection) {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            needsRoleSelection: true,
            updatedAt: serverTimestamp(),
          });
          debugLog('Updated existing user document with needsRoleSelection: true');
        }
        setProgress(90);
      }
      
      setProgress(100);
      // Auth state listener will handle the rest
    } catch (error: any) {
      console.error('Native Google Sign-In error:', error);
      setLoading(false);
      setProgress(0);
      throw new Error(error.message || 'Failed to sign in with Google. Please try again.');
    }
    // Note: setLoading(false) is handled by the auth state listener
    */
  };

  const signInWithGoogleRedirect = async () => {
    // Check platform - disable only on iOS
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      throw new Error('Google Sign-In redirect is unavailable on iOS. Please use email/password sign-in or visit the web version at www.streakerbymx.com');
    }

    // Web platform - use redirect (this works!)
    setLoading(true);
    setProgress(0);
    try {
      setProgress(20);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      setProgress(50);
      debugLog('Calling signInWithRedirect - page will redirect to Google');
      
      // signInWithRedirect doesn't return - it redirects the page
      // The result will be processed by getRedirectResult in useEffect
      await signInWithRedirect(auth, provider);
      // This line should never execute because redirect happens
      console.warn('signInWithRedirect returned (unexpected) - redirect may not have worked');
      
      setProgress(100);
    } catch (error: any) {
      console.error('Google Sign-In redirect error:', error);
      setLoading(false);
      setProgress(0);
      throw new Error(error.message || 'Failed to redirect to Google sign-in. Please try again.');
    }
  };

  const updateUserRole = async (role: 'coach' | 'athlete') => {
    if (!user) {
      throw new Error('No user logged in');
    }

    setLoading(true);
    setProgress(0);
    try {
      setProgress(30);
      // Update the user's role in Firestore and remove the role selection flag
      await updateDoc(doc(db, 'users', user.id), {
        role,
        needsRoleSelection: false, // Remove the flag
        updatedAt: serverTimestamp(),
      });
      setProgress(70);

      // Update local user state
      setUser({
        ...user,
        role,
        updatedAt: new Date() as any,
      });

      // Clear the role selection flag
      setNeedsRoleSelection(false);
      setProgress(100);
      
      // Set loading to false after successful role update
      setTimeout(() => {
        setLoading(false);
      }, 200);
    } catch (error) {
      setLoading(false);
      setProgress(0);
      throw error;
    }
  };

  const updateUserTeamId = (teamId: string) => {
    if (!user) {
      console.warn('updateUserTeamId: No user logged in');
      return;
    }
    
    debugLog('updateUserTeamId: Updating user teamId to:', teamId);
    setUser({
      ...user,
      teamId,
    });
  };

  const refreshCurrentUser = async () => {
    const currentUserId = user?.id || auth.currentUser?.uid;
    if (!currentUserId) return;
    try {
      const freshUser = await userService.getUserById(currentUserId);
      if (freshUser) {
        setUser(freshUser);
      }
    } catch (error) {
      console.warn('[AuthContext] refreshCurrentUser failed:', error);
    }
  };

  const signOut = async () => {
    setLoading(true);
    setProgress(0);
    try {
      setProgress(20);
      debugLog('Starting sign-out...');
      
      const platform = Capacitor.getPlatform();
      
      // On iOS, Firebase SDK sign-out might hang, so add timeout
      if (platform === 'ios') {
        debugLog('iOS platform detected - using sign-out with timeout');
        setProgress(40);
        
        const signOutPromise = firebaseSignOut(auth);
        const signOutTimeout = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Sign-out timeout')), 3000)
        );
        
        try {
          await Promise.race([signOutPromise, signOutTimeout]);
          debugLog('SDK sign-out succeeded');
        } catch (timeoutError: any) {
          console.warn('SDK sign-out timed out or failed, manually clearing state:', timeoutError);
          // Manually clear auth state if SDK fails
          // Note: We can't manually clear auth.currentUser, but we can clear our app state
        }
      } else {
        // For web/Android, use normal SDK sign-out
        setProgress(50);
        await firebaseSignOut(auth);
      }
      
      // Always clear our app state regardless of SDK result
      debugLog('Clearing user state...');
      setProgress(80);
      setUser(null);
      setNeedsRoleSelection(false);
      setSessionRestored(false); // Reset session restored flag
      
      // Clear any stored tokens
      try {
        await clearTokens();
      } catch (e) {
        console.warn('Could not clear tokens:', e);
      }
      
      setProgress(100);
      debugLog('Sign-out completed');
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 200);
    } catch (error) {
      console.error('Sign-out error:', error);
      // Even if there's an error, clear our app state
      setUser(null);
      setNeedsRoleSelection(false);
      setLoading(false);
      setProgress(0);
      // Don't throw - just log the error and continue
      // Sign-out should always succeed from the app's perspective
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    progress,
    needsRoleSelection,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithGoogleRedirect,
    updateUserRole,
    updateUserTeamId,
    refreshCurrentUser,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

